import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../hooks/useTelegram';
import { useSession } from '../../hooks/useSession';
import { useToast } from '../../store/toast';
import { createDefaultWallet } from '../../lib/wallet';

import cardBack from '../../assets/figma/onboarding/s1-card-back.png';
import cardMid from '../../assets/figma/onboarding/s1-card-mid.png';
import cardFront from '../../assets/figma/onboarding/s1-card-front.png';
import txList from '../../assets/figma/onboarding/s1-txlist.png';
import s2Phone from '../../assets/figma/onboarding/s2-phone.png';
import s2Visa from '../../assets/figma/onboarding/s2-visa.png';
import s3Phone from '../../assets/figma/onboarding/s3-phone.png';
import s3QrFrame from '../../assets/figma/onboarding/s3-qr-frame.png';

/** A positioned illustration layer. left/top/width are % of the illustration box
 *  (measured from Figma: group 402 wide). delay staggers the entrance. */
type Layer = {
  src: string;
  left: number;
  top: number;
  width: number;
  anim: string;
  delay: number;
  rotate?: number;
  float?: boolean;
  fade?: boolean; // fade the bottom of this layer out (the phone dissolves down)
  fadeTop?: boolean; // also fade the top out (phone dissolves at both ends)
  pulse?: boolean; // gentle opacity pulse (the scanner frame)
};

// groupH = the Figma illustration-group height (width is always 402), so each
// slide's aspect ratio and layer %s line up exactly with the design.
type ScanBox = { left: number; top: number; width: number; height: number };
// `scale` visually enlarges an illustration so all three read at the same size
// (slide 3's portrait terminal is narrower, so it needs a bump).
const SLIDES: {
  titleKey: string;
  groupH: number;
  scale?: number;
  offsetY?: number; // vertical nudge (% of the illustration) to align across slides
  layers: Layer[];
  scan?: ScanBox;
}[] = [
  {
    titleKey: 'onboarding.slide1Title',
    groupH: 532,
    layers: [
      { src: cardBack, left: 18.4, top: 10.9, width: 63.2, anim: 'ob-card-in', delay: 0.05, rotate: -4 },
      { src: cardMid, left: 13.2, top: 13.5, width: 73.6, anim: 'ob-card-in', delay: 0.15, rotate: 3 },
      { src: cardFront, left: 7.7, top: 16.2, width: 84.6, anim: 'ob-card-in', delay: 0.28, float: true },
      { src: txList, left: 7.7, top: 56.4, width: 84.6, anim: 'ob-rise', delay: 0.42 },
    ],
  },
  {
    titleKey: 'onboarding.slide2Title',
    groupH: 519,
    layers: [
      { src: s2Phone, left: 14.9, top: 9.1, width: 70.1, anim: 'ob-rise', delay: 0.05, fade: true },
      { src: s2Visa, left: 9.5, top: 21.6, width: 81.1, anim: 'ob-visa-in', delay: 0.3, float: true },
    ],
  },
  {
    titleKey: 'onboarding.slide3Title',
    groupH: 533,
    scale: 1.22,
    offsetY: 12,
    scan: { left: 32.5, top: 20.5, width: 35.5, height: 24.5 },
    layers: [
      // POS terminal — static (no float); fades only at the TOP (bezel dissolves
      // upward), bottom keeps its full stacked base like the Figma render.
      { src: s3Phone, left: 17.9, top: -6.9, width: 64.2, anim: 'ob-rise', delay: 0.08, fadeTop: true },
      // blue QR scanner frame (Figma node 2:5630, crisp — no glow) — blinks.
      { src: s3QrFrame, left: 30.4, top: 17.9, width: 39.8, anim: 'ob-text-in', delay: 0.5, pulse: true },
    ],
  },
];

function Illustration({ slide, index }: { slide: (typeof SLIDES)[number]; index: number }) {
  return (
    // key forces the animations to replay each time the slide changes
    <div
      key={index}
      className="relative mx-auto w-full"
      style={{
        aspectRatio: `402 / ${slide.groupH}`,
        transform: `translateY(${slide.offsetY ?? 0}%) scale(${slide.scale ?? 1})`,
      }}
    >
      {slide.layers.map((l, i) => (
        <div
          key={i}
          className="ob-anim absolute"
          style={{
            left: `${l.left}%`,
            top: `${l.top}%`,
            width: `${l.width}%`,
            animationName: l.anim,
            animationDelay: `${l.delay}s`,
            ['--r' as string]: `${l.rotate ?? 0}deg`,
          }}
        >
          <div
            style={
              l.float
                ? { animation: 'ob-float 4s ease-in-out infinite', animationDelay: `${l.delay + 0.7}s` }
                : l.pulse
                  ? { animation: 'ob-blink 1.4s ease-in-out 0.9s infinite' }
                  : undefined
            }
          >
            <img src={l.src} alt="" className="w-full" draggable={false} style={maskStyle(l)} />
          </div>
        </div>
      ))}

      {/* scan light — a modern sweeping band (soft gradient trail + a crisp
          leading line) that travels up and down over the QR frame only */}
      {slide.scan && (
        <div
          className="pointer-events-none absolute overflow-hidden rounded-[10%]"
          style={{
            left: `${slide.scan.left}%`,
            top: `${slide.scan.top}%`,
            width: `${slide.scan.width}%`,
            height: `${slide.scan.height}%`,
          }}
        >
          <div
            className="absolute inset-x-0"
            style={{ height: '38%', animation: 'ob-scanline 1.9s cubic-bezier(0.45,0,0.55,1) 1s infinite alternate' }}
          >
            {/* soft gradient trail */}
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to bottom, rgba(33,149,241,0) 0%, rgba(33,149,241,0.18) 100%)' }}
            />
            {/* crisp leading line */}
            <div
              className="absolute inset-x-0 bottom-0 h-[2px]"
              style={{ background: 'rgb(var(--accent))', boxShadow: '0 0 6px 0.5px rgba(33,149,241,0.7)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Fade mask for a layer: top-only, bottom-only, or both ends. */
function maskStyle(l: Layer): React.CSSProperties | undefined {
  let g: string | undefined;
  if (l.fadeTop && l.fade)
    g = 'linear-gradient(to bottom, transparent 2%, #000 22%, #000 62%, transparent 88%)';
  else if (l.fadeTop) g = 'linear-gradient(to bottom, transparent 0%, transparent 8%, #000 46%, #000 100%)';
  else if (l.fade) g = 'linear-gradient(to bottom, #000 62%, transparent 88%)';
  return g ? { maskImage: g, WebkitMaskImage: g } : undefined;
}

export function Onboarding({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const { token, ready } = useSession();
  const showToast = useToast((s) => s.show);
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const slide = SLIDES[i]!;
  const last = i === SLIDES.length - 1;

  const next = async () => {
    haptic();
    if (!last) {
      setI(i + 1);
      return;
    }
    if (busy) return;
    // In real Telegram, wait for the session before creating the wallet.
    if (ready && !token) {
      showToast('Connecting…');
      return;
    }
    setBusy(true);
    try {
      // Final slide: create the default TRON wallet (seed → CloudStorage, key →
      // server). The gate then sees the wallet and enters the app.
      if (token) await createDefaultWallet(token);
      setBusy(false);
      onDone();
    } catch (e) {
      // Surface the failure instead of silently looping back to onboarding.
      setBusy(false);
      showToast(`Wallet setup failed: ${(e as Error)?.message ?? 'error'}`);
    }
  };

  return (
    <div className="relative mx-auto flex min-h-screen w-full max-w-[440px] flex-col bg-surface pt-safe">
      {/* illustration — vertically centred in the available space */}
      <div className="flex flex-1 items-center overflow-hidden px-4">
        <Illustration slide={slide} index={i} />
      </div>

      {/* title */}
      <div className="px-4">
        <h1
          key={`t-${i}`}
          className="ob-anim mx-auto max-w-[340px] text-center text-[24px] font-bold leading-[1.28] text-ink"
          style={{ animationName: 'ob-text-in', animationDelay: '0.15s' }}
        >
          {t(slide.titleKey)}
        </h1>

        {/* page indicator */}
        <div className="mt-4 flex items-center justify-center gap-2">
          {SLIDES.map((_, d) => (
            <span
              key={d}
              className="h-[6px] rounded-full transition-all duration-300"
              style={{
                width: d === i ? 16 : 6,
                backgroundColor: d === i ? 'rgb(var(--accent))' : 'rgba(33,149,241,0.25)',
              }}
            />
          ))}
        </div>
      </div>

      {/* continue button */}
      <div className="px-4 pb-[max(44px,var(--tg-safe-bottom))] pt-8">
        <button
          onClick={next}
          disabled={busy}
          className="flex h-14 w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white shadow-[0px_0px_20px_rgba(0,0,0,0.12)] transition-transform active:scale-[0.99] disabled:opacity-80"
        >
          {busy ? (
            <span className="size-6 animate-spin rounded-full border-[3px] border-card/40 border-t-white" />
          ) : (
            t('common.continue')
          )}
        </button>
      </div>
    </div>
  );
}
